import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { layout } from "../utils/router";

const isDevOrLocalEnvironment = (): boolean => {
  const apiHost = (import.meta.env.VITE_APP_API_HOST as string) || "";
  return apiHost.includes("rca-dev") || apiHost.includes("localhost");
};

const isAdminPath = (path: string): boolean => {
  return path.startsWith("/admin") || path.startsWith("/stores-manager");
};

const adminRoutes: RouteRecordRaw[] = [
  {
    path: "/admin",
    name: "Admin",
    meta: {
      title: "Risk Analytics - Admin"
    },
    component: () =>
      import(/* webpackChunkName: "admin" */ "@/views/Admin.vue")
  },
  {
    path: "/stores-manager",
    name: "Manage stores",
    meta: {
      title: "Admin - Store Manager"
    },
    component: () =>
      import(/* webpackChunkName: "store-manager" */ "@/views/StoreManager.vue")
  }
];

const childRoutes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: "/signin"
  },
  {
    path: "/riskpatterns",
    name: "RiskPatterns",
    meta: {
      title: "Risk Analytics - Risk Patterns"
    },
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "home" */ "@/views/RiskPatternsHome.vue")
  },
  {
    path: "/signin",
    name: "LandingPage",
    component: () =>
      import(/* webpackChunkName: "landing" */ "@/views/Landing.vue")
  },
  {
    path: "/landing",
    name: "Dashboard",
    component: () =>
      import(/* webpackChunkName: "landing" */ "@/views/DashboardView.vue")
  },
  {
    path: "/case-details/:caseid",
    name: "CaseDetails",
    meta: {
      title: "Risk Analytics - Case Details"
    },
    component: () =>
      import(/* webpackChunkName: "casedetails" */ "@/views/CaseDetails.vue")
  },
  {
    path: "/riskpattern-reports",
    name: "StatusReport",
    meta: {
      title: "Status Report"
    },
    component: () =>
      import(/* webpackChunkName: "statusreport" */ "@/views/ReportPage.vue")
  },
  {
    path: "/control-reports",
    name: "ControlReport",
    meta: {
      title: "Control Report"
    },
    component: () =>
      import(
        /* webpackChunkName: "statusreport" */ "@/views/ControlsReport.vue"
      )
  },
  {
    path: "/controls",
    name: "RiskControls",
    meta: {
      title: "Risk Controls"
    },
    component: () =>
      import(/* webpackChunkName: "statusreport" */ "@/views/ControlsHome.vue")
  },
  {
    path: "/controls/:controltype",
    name: "ControlsBacklog",
    meta: {
      title: "Controls Backlog"
    },
    component: () =>
      import(
        /* webpackChunkName: "controlsbacklog" */ "@/views/ControlsBacklogWrapper.vue"
      )
  },
  {
    path: "/controls/:controltype/:caseid",
    name: "ControlDetails",
    meta: {
      title: "Control Details"
    },
    component: () =>
      import(
        /* webpackChunkName: "controlsbacklog" */ "@/views/ControlDetails.vue"
      )
  },
  {
    path: "/aml-reports",
    name: "AMLReports",
    meta: {
      title: "AML Report"
    },
    component: () =>
      import(
        /* webpackChunkName: "statusreport" */ "@/views/AMLReport.vue"
      )
  },
  {
    path: "/:catchAll(.*)",
    name: "NotFound",
    component: () =>
      import(/* webpackChunkName: "not-found" */ "@/views/NotFound.vue")
  }
];

const layoutRoute = layout(childRoutes);
layoutRoute.name = "AppLayout";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: (to, _from, savedPosition) => {
    if (to.hash) return { el: to.hash };
    if (savedPosition) return savedPosition;

    return { top: 0, left: 0 };
  },
  routes: [layoutRoute]
});

if (isDevOrLocalEnvironment()) {
  adminRoutes.forEach((route) => {
    router.addRoute("AppLayout", route);
  });
}

router.beforeEach((to, _from, next) => {
  if (isAdminPath(to.path) && !isDevOrLocalEnvironment()) {
    return next({ path: "/landing" });
  }

  document.title =
    to.meta && typeof to.meta.title === "string"
      ? to.meta.title
      : (import.meta.env.VITE_APP_TITLE as string) || "Risk Analytics";
  return next();
});

export default router;
